import { getHexTileMapKey } from '../../hexmap/model/keys';
import type { HexTileMapKey } from '../../hexmap/model/types';
import { tickDerived } from '../derived';
import { deployAll, type Occupancy, storageCapacity } from '../occupancy';
import { mintOrder } from '../territory';
import type { EntityId, PlayerId, SystemFn } from '../types';
import { canAttack } from './pursuit';

/**
 * Barracks storage defends its city: when an enemy fighter its owner can see
 * stands in the territory of the city a barracks belongs to, every unit
 * inside it comes out onto the nearest free tiles (see occupancy.ts
 * `deployAll`). Damage to the city or barracks does the same (combat.ts).
 * Runs before pursuit and movement, so they step out this tick.
 */
const storage: SystemFn = (state, ctx) => {
  const stores = new Set<EntityId>();
  for (const u of Object.values(state.units)) if (u.inside) stores.add(u.inside);
  if (stores.size === 0) return;

  const derived = tickDerived(state, ctx);
  const owners = derived.owners();
  // Owner → tiles they see, looked up only when an enemy fighter is in a
  // territory, and kept for the pass (deploying doesn't change them).
  const vision = new Map<PlayerId, ReadonlySet<HexTileMapKey>>();
  const threatened = (cityId: EntityId, owner: PlayerId): boolean => {
    for (const u of Object.values(state.units)) {
      if (u.owner === owner || u.inside || !canAttack(ctx.rules, u)) continue;
      const key = getHexTileMapKey(u.pos);
      if (owners.get(key) !== cityId) continue;
      let seen = vision.get(owner);
      if (!seen) {
        seen = derived.vision(owner);
        vision.set(owner, seen);
      }
      if (seen.has(key)) return true;
    }
    return false;
  };

  let occ: Occupancy | undefined;
  for (const id of [...stores].sort((a, b) => mintOrder(a) - mintOrder(b))) {
    const b = state.buildings[id];
    if (!b || storageCapacity(ctx.rules, b) === 0) continue;
    const cityId = owners.get(getHexTileMapKey(b.pos));
    if (!cityId || !threatened(cityId, b.owner)) continue;
    occ ??= derived.occupancy();
    deployAll(state, ctx, occ, id);
  }
};

export { storage };
